import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { WebsocketService } from 'shared';
import { TEntityType } from '../model/types';
import { IReminder } from '../model/reminder.model';
import { ReminderCenterService } from './reminder-center.service';

export interface ReminderRefreshEvent {
  entityId: string;
  typeOfReminder: TEntityType;
  source: 'own' | 'all';
  raw: any;
}

@Injectable({
  providedIn: 'root',
})
export class ReminderWatcherService implements OnDestroy {
  private readonly isBrowser: boolean;
  private ownNotificationsSub?: Subscription;
  private allNotificationsSub?: Subscription;
  private readonly reminderRefreshSubject = new Subject<ReminderRefreshEvent>();
  private lastPopupReminderKey: string | null = null;
  private lastPopupReminderAt = 0;
  readonly reminderRefresh$ = this.reminderRefreshSubject.asObservable();

  constructor(
    private readonly websocketService: WebsocketService,
    private readonly reminderCenter: ReminderCenterService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  start(): void {
    if (
      !this.isBrowser ||
      this.ownNotificationsSub ||
      this.allNotificationsSub
    ) {
      console.log('[ReminderWatcherService] start() skipped', {
        isBrowser: this.isBrowser,
        ownNotificationsSubscribed: !!this.ownNotificationsSub,
        allNotificationsSubscribed: !!this.allNotificationsSub,
      });
      return;
    }

    console.log(
      '[ReminderWatcherService] start() subscribing to reminder-related notification channels'
    );
    this.websocketService.connect();

    this.ownNotificationsSub = this.websocketService
      .getOwnNotifications()
      .subscribe({
        next: (notification) => {
          console.log(
            '[ReminderWatcherService] own notification received',
            notification
          );
          this.handleNotification(notification, 'own');
        },
        error: (error) => {
          console.error(
            '[ReminderWatcherService] own notification stream error',
            error
          );
        },
      });

    this.allNotificationsSub = this.websocketService
      .getAllNotifications()
      .subscribe({
        next: (notification) => {
          console.log(
            '[ReminderWatcherService] broadcast notification received',
            notification
          );
          this.handleNotification(notification, 'all');
        },
        error: (error) => {
          console.error(
            '[ReminderWatcherService] broadcast notification stream error',
            error
          );
        },
      });
  }

  stop(): void {
    console.log('[ReminderWatcherService] stop() unsubscribing from notification channels');
    this.ownNotificationsSub?.unsubscribe();
    this.ownNotificationsSub = undefined;
    this.allNotificationsSub?.unsubscribe();
    this.allNotificationsSub = undefined;
    this.websocketService.disconnect();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private handleNotification(notification: any, source: 'own' | 'all'): void {
    const payload = notification?.body ?? notification;
    if (!this.isReminderNotification(payload)) {
      return;
    }

    const event = this.toReminderRefreshEvent(notification, source);
    if (event) {
      this.reminderRefreshSubject.next(event);
    }

    const reminder = this.toPopupReminder(payload);
    if (reminder && this.shouldShowPopup(reminder)) {
      this.reminderCenter.show(reminder);
    }
  }

  private toReminderRefreshEvent(
    notification: any,
    source: 'own' | 'all'
  ): ReminderRefreshEvent | null {
    const payload = notification?.body ?? notification;
    const reminderPayload = this.extractReminderPayload(payload);
    const entityId = String(
      reminderPayload?.entityId ??
        payload?.entityId ??
        payload?.data?.entityId ??
        ''
    ).trim();
    const typeOfReminder = this.normalizeReminderType(
      reminderPayload?.typeOfReminder ??
        payload?.typeOfReminder ??
        payload?.data?.typeOfReminder
    );

    if (!entityId || !typeOfReminder) {
      return null;
    }

    return {
      entityId,
      typeOfReminder,
      source,
      raw: payload,
    };
  }

  private normalizeReminderType(
    value: string | null | undefined
  ): TEntityType | null {
    const normalizedValue = String(value ?? '')
      .trim()
      .toUpperCase();

    switch (normalizedValue) {
      case 'ENQUIRY':
        return 'ENQUIRY';
      case 'LEAD':
        return 'LEAD';
      case 'WHATSAPP':
        return 'WHATSAPP';
      case 'TICKET':
        return 'TICKET';
      case 'USER':
        return 'USER';
      default:
        return null;
    }
  }

  private toPopupReminder(payload: any): IReminder | null {
    const reminderPayload = this.extractReminderPayload(payload);
    if (!reminderPayload) {
      return null;
    }

    const reminderId = String(
      reminderPayload?.reminderId ?? payload?.reminderId ?? ''
    ).trim();
    const entityId = String(
      reminderPayload?.entityId ?? payload?.entityId ?? ''
    ).trim();
    const completionTime = String(
      reminderPayload?.completionTime ?? payload?.completionTime ?? ''
    ).trim();
    const typeOfReminder = this.normalizeReminderType(
      reminderPayload?.typeOfReminder ??
        payload?.typeOfReminder ??
        payload?.type
    );
    const title = String(
      reminderPayload?.title ?? payload?.title ?? payload?.message ?? 'Reminder'
    ).trim();
    const description = String(
      reminderPayload?.description ?? payload?.description ?? payload?.message ?? ''
    ).trim();

    if (!reminderId || !entityId || !completionTime || !typeOfReminder) {
      return null;
    }

    return {
      reminderId,
      entityId,
      completionTime,
      typeOfReminder,
      title,
      description,
      status: String(
        reminderPayload?.status ?? payload?.status ?? 'PENDING'
      ).trim(),
      userId:
        reminderPayload?.userId ??
        payload?.userId ??
        null,
      createdBy: reminderPayload?.createdBy ?? payload?.createdBy,
      createdDate: reminderPayload?.createdDate ?? payload?.createdDate,
      modifiedBy: reminderPayload?.modifiedBy ?? payload?.modifiedBy,
      modifiedDate: reminderPayload?.modifiedDate ?? payload?.modifiedDate,
      isPinned: Boolean(
        reminderPayload?.isPinned ?? payload?.isPinned ?? false
      ),
      phoneNumber: reminderPayload?.phoneNumber ?? payload?.phoneNumber,
      customerName: reminderPayload?.customerName ?? payload?.customerName,
    };
  }

  private extractReminderPayload(payload: any): any | null {
    if (this.looksLikeReminderPayload(payload?.reminder)) {
      return payload.reminder;
    }

    if (this.looksLikeReminderPayload(payload?.data?.reminder)) {
      return payload.data.reminder;
    }

    if (this.looksLikeReminderPayload(payload?.data)) {
      return payload.data;
    }

    if (this.looksLikeReminderPayload(payload)) {
      return payload;
    }

    return null;
  }

  private looksLikeReminderPayload(payload: any): boolean {
    return Boolean(
      payload &&
        typeof payload === 'object' &&
        (
          payload.reminderId ||
          payload.entityId ||
          payload.completionTime ||
          payload.typeOfReminder
        )
    );
  }

  private isReminderNotification(payload: any): boolean {
    const wrapperType = String(payload?.type ?? '')
      .trim()
      .toUpperCase();
    const notificationType = String(payload?.notification?.type ?? '')
      .trim()
      .toUpperCase();

    return (
      wrapperType === 'REMINDER_NOTIFICATION' ||
      notificationType === 'REMINDER' ||
      !!this.extractReminderPayload(payload)
    );
  }

  private shouldShowPopup(reminder: IReminder): boolean {
    const popupKey = `${reminder.reminderId}:${reminder.completionTime}:${reminder.status}`;
    const now = Date.now();

    if (
      this.lastPopupReminderKey === popupKey &&
      now - this.lastPopupReminderAt < 2000
    ) {
      return false;
    }

    this.lastPopupReminderKey = popupKey;
    this.lastPopupReminderAt = now;
    return true;
  }
}
